import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { CapabilityGate } from '../../../components/CapabilityGate';
import {
  useCreateNativeBooking,
  useSchedulingAvailability,
  useSchedulingServices,
} from '../../../hooks/use-scheduling';
import { useToastStore } from '../../../stores/toast-store';

import type { SchedulingSlotWire } from '../../../lib/api-client';
import type { JSX } from 'react';

const DAY_MS = 24 * 60 * 60_000;
const DAY_COUNT = 7;

/** Rótulo curto de dia ("Qua 02/07") no fuso local — espelha `SchedulingAvailabilityPanel`. */
function dayLabel(ms: number): string {
  const d = new Date(ms);
  const wd = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.getDay()] ?? '';
  return `${wd} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

export interface ConversationBookingModalProps {
  conversationId: string;
  personId?: string | null;
  bondId?: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * ConversationBookingModal — ADR-155 Fase 4 (§3.1) "Agendar" pela CONVERSA.
 *
 * Booking nativo (serviço → dia → slot) passando `conversationId` (+
 * `personId`/`bondId` da conversa quando existirem) — o foco do episódio
 * (D4) nasce SERVER-SIDE junto com o booking (mecânica do PR C, que hoje só
 * a IA usa pelo conductor). Espelho 1:1 do fluxo do `BookingSheet` mobile e
 * do `SchedulingAvailabilityPanel` web (mesmos hooks `use-scheduling` —
 * PROIBIDO duplicar a máquina de slots). Gated `scheduling-native`.
 *
 * Convive com o "+ Agendamento" legado do header (`AppointmentCreateModal`,
 * `POST /v1/appointments` sem foco) — este é o caminho que grava o foco.
 */
export function ConversationBookingModal({
  conversationId,
  personId = null,
  bondId = null,
  open,
  onClose,
}: ConversationBookingModalProps): JSX.Element | null {
  const servicesQ = useSchedulingServices(open);
  const [serviceId, setServiceId] = useState('');
  const [dayOffset, setDayOffset] = useState(0);
  const book = useCreateNativeBooking();
  const qc = useQueryClient();

  const services = servicesQ.data?.data ?? [];
  const effectiveServiceId = serviceId !== '' ? serviceId : (services[0]?.id ?? '');

  const dayStartMs = new Date().setHours(0, 0, 0, 0) + dayOffset * DAY_MS;
  const from = new Date(Math.max(dayStartMs, Date.now())).toISOString();
  const to = new Date(dayStartMs + DAY_MS).toISOString();

  const availabilityQ = useSchedulingAvailability(
    { serviceOfferingId: effectiveServiceId, from, to, limit: 60 },
    open && effectiveServiceId !== '',
  );
  const slots = availabilityQ.data?.data ?? [];

  const handleBook = (slot: SchedulingSlotWire): void => {
    const svc = services.find((s) => s.id === effectiveServiceId);
    book.mutate(
      {
        serviceOfferingId: effectiveServiceId,
        startsAt: slot.startsAt,
        conversationId,
        ...(personId !== null && { personId }),
        ...(bondId !== null && { bondId }),
      },
      {
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
          useToastStore.getState().push({
            variant: 'success',
            title: 'Agendamento criado',
            description: `${svc?.name ?? 'Serviço'} em ${dayLabel(dayStartMs)} ${timeLabel(slot.startsAt)}. A coleta desta conversa passou a ser focada neste agendamento.`,
            durationMs: 4500,
          });
          onClose();
        },
        onError: (err) => {
          const taken = err instanceof Error && /slot_unavailable|409|conflict/i.test(err.message);
          useToastStore.getState().push({
            variant: taken ? 'info' : 'error',
            title: taken ? 'Horário acabou de ser tomado' : 'Falha ao agendar',
            description: taken
              ? 'Esse horário foi ocupado. Atualizamos a lista — escolha outro.'
              : err instanceof Error
                ? err.message
                : 'Erro desconhecido',
            durationMs: 5500,
          });
          void availabilityQ.refetch();
        },
      },
    );
  };

  // Guard de render DEPOIS de todos os hooks (rules-of-hooks) — os hooks
  // acima já são gateados por `enabled`/`open` (nenhum fetch de graça
  // enquanto fechado); só a árvore JSX é suprimida aqui.
  if (!open) return null;

  return (
    <CapabilityGate capability="scheduling-native">
      <div
        role="presentation"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          role="dialog"
          aria-label="Agendar"
          className="w-full max-w-lg rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-4 text-lg font-semibold">Agendar</h2>

          {servicesQ.isLoading ? (
            <p className="text-sm text-text-secondary">Carregando serviços…</p>
          ) : services.length === 0 ? (
            <p className="text-sm text-text-secondary">
              Nenhum serviço de agenda nativa cadastrado neste tenant.
            </p>
          ) : (
            <>
              <div className="mb-1 text-xs font-medium" style={{ color: 'var(--fg-3)' }}>
                Serviço
              </div>
              <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Serviço">
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="rounded border px-3 py-1.5 text-sm"
                    style={{
                      borderColor:
                        s.id === effectiveServiceId ? 'var(--accent-fg)' : 'var(--border-default)',
                      background:
                        s.id === effectiveServiceId ? 'var(--accent-bg)' : 'var(--bg-surface-2)',
                    }}
                    onClick={() => setServiceId(s.id)}
                  >
                    {s.name} ({s.defaultDurationMinutes}min)
                  </button>
                ))}
              </div>

              <div className="mb-1 text-xs font-medium" style={{ color: 'var(--fg-3)' }}>
                Dia
              </div>
              <div className="mb-3 flex flex-wrap gap-1" role="group" aria-label="Dia">
                {Array.from({ length: DAY_COUNT }, (_, off) => off).map((off) => (
                  <button
                    key={off}
                    type="button"
                    className={`rounded border px-2 py-1 text-xs ${off === dayOffset ? 'font-semibold' : ''}`}
                    style={{
                      borderColor: off === dayOffset ? 'var(--accent-fg)' : 'var(--border-default)',
                      background: off === dayOffset ? 'var(--accent-bg)' : 'var(--bg-surface-2)',
                    }}
                    onClick={() => setDayOffset(off)}
                  >
                    {dayLabel(new Date().setHours(0, 0, 0, 0) + off * DAY_MS)}
                  </button>
                ))}
              </div>

              <div className="mb-1 text-xs font-medium" style={{ color: 'var(--fg-3)' }}>
                Horário
              </div>
              {availabilityQ.isLoading ? (
                <p className="text-sm text-text-secondary">Calculando horários livres…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-text-secondary">
                  Sem horários livres para {dayLabel(dayStartMs)}. Tente outro dia.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="conversation-booking-slots">
                  {slots.map((slot) => (
                    <button
                      key={slot.startsAt}
                      type="button"
                      className="rounded border px-3 py-2 text-sm"
                      style={{
                        borderColor: 'var(--border-default)',
                        background: 'var(--bg-surface-2)',
                      }}
                      onClick={() => handleBook(slot)}
                      disabled={book.isPending}
                    >
                      <span className="font-mono">{timeLabel(slot.startsAt)}</span>
                      {slot.remainingCapacity > 1 && (
                        <span className="ml-2 text-xs" style={{ color: 'var(--fg-3)' }}>
                          {slot.remainingCapacity} vagas
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={book.isPending}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
